import { useState } from 'react';
import { Heading, Text, Button, Dialog, IconButton } from 'frosted-ui';
import { XMark16, ShieldCheckmark16 } from '@frosted-ui/icons';
import { useTerms } from '../contexts/TermsContext';

interface TermsAcceptanceModalProps {
  onCancel: () => void;
  onAccepted?: () => void;
}

export default function TermsAcceptanceModal({ onCancel, onAccepted }: TermsAcceptanceModalProps) {
  const { acceptTerms } = useTerms();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleAccept() {
    setSubmitting(true);
    setError(null);
    try {
      await acceptTerms();
      onAccepted?.();
    } catch (err) {
      setError((err as Error).message || 'Failed to record acceptance. Please try again.');
      setSubmitting(false);
    }
  }

  return (
    <Dialog.Root open onOpenChange={(open: boolean) => !open && !submitting && onCancel()}>
      <Dialog.Content style={{ maxWidth: 460 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginBottom: 'var(--space-4)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: 'var(--radius-3)',
              backgroundColor: 'var(--accent-a3)',
            }}
          >
            <ShieldCheckmark16 style={{ color: 'var(--accent-9)', width: 24, height: 24 }} />
          </div>
          <IconButton
            variant="ghost"
            color="gray"
            size="1"
            onClick={onCancel}
            disabled={submitting}
            aria-label="Close"
          >
            <XMark16 />
          </IconButton>
        </div>

        <Heading size="5" style={{ marginBottom: 'var(--space-2)' }}>
          Review and accept our terms
        </Heading>
        <Text color="gray" size="3" style={{ display: 'block', marginBottom: 'var(--space-5)' }}>
          Before going live, please review and accept our{' '}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--accent-11)', textDecoration: 'underline' }}
          >
            Terms of Service
          </a>{' '}
          and{' '}
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--accent-11)', textDecoration: 'underline' }}
          >
            Privacy Policy
          </a>
          . These govern how Paywall Lab sets up prices on your behalf and how we handle related
          data.
        </Text>

        {error && (
          <Text color="red" size="2" style={{ display: 'block', marginBottom: 'var(--space-3)' }}>
            {error}
          </Text>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <Button
            variant="solid"
            size="3"
            onClick={handleAccept}
            loading={submitting}
            style={{ width: '100%' }}
          >
            I Agree
          </Button>
          <Button
            variant="soft"
            color="gray"
            size="3"
            onClick={onCancel}
            disabled={submitting}
            style={{ width: '100%' }}
          >
            Cancel
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
